<script>
  import { onMount, tick } from "svelte";
  import { useUrlParams } from "@stores/urlStore.js";
  import { ROUTES } from "@utils/routes.js";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { Accordion, SectionWrapper } from "paxray-components-svelte";
  import CardColumn from "@components/CardColumn.svelte";
  import Header from "@components/Header.svelte";
  import ProgressIndicator from "@components/ProgressIndicator.svelte";
  import ProgressStep from "@components/ProgressStep.svelte";
  import SideKPI from "@components/sideKPI.svelte";
  import CheckIcon from "@components/layout/sideNav/icons/CheckIcon.svelte";
  import CheckEmptyIcon from "@components/layout/sideNav/icons/CheckEmptyIcon.svelte";
  import CheckXIcon from "@components/layout/sideNav/icons/CheckXIcon.svelte";
  import SettingsIcon from "@components/layout/sideNav/icons/SettingsIcon.svelte";
  import TaskRegistrationIcon from "@components/layout/sideNav/icons/TaskRegistrationIcon.svelte";

  const urlParams = useUrlParams();

  let survey = null;
  let analysisSnapshot = null;
  let loading = true;
  let loadError = false;
  let progressSectionElement;
  let progressCardHeight = "";

  $: surveyId = $urlParams.survey;
  // A task survey is a web survey like the Potential Scout; its scope and
  // questions live in task_feedback_config instead of survey_tasks/questions.
  $: taskSurvey = survey?.survey_type === "task_feedback";
  $: feedbackConfig = survey?.task_feedback_config || {};
  $: sourceProjectId = feedbackConfig.source_project_id || "";
  $: surveyTasks = taskSurvey
    ? feedbackConfig.tasks || []
    : survey?.survey_tasks || [];
  $: feedbackKpis = feedbackConfig.kpis || [];
  $: finished = survey?.status === "FINISHED";
  $: surveyLocked =
    finished || (survey?.status === "RUNNING" && setupComplete);
  $: participants = survey?.survey_users || [];
  $: outstanding = participants.filter(
    (participant) => !participant.invitation_email_sent,
  ).length;
  $: pending = participants.filter(
    (participant) =>
      participant.invitation_email_sent && !participant.survey_answered,
  ).length;
  $: completed = participants.filter(
    (participant) => participant.survey_answered,
  ).length;
  $: responseRate = participants.length
    ? Math.round((completed / participants.length) * 100)
    : 0;
  $: scopeConfigured = surveyTasks.length > 0;
  $: participantsConfigured = participants.length > 0;
  $: settingsConfigured = taskSurvey
    ? Boolean(survey?.start_date && survey?.end_date && feedbackKpis.length)
    : Boolean(
        survey?.start_date &&
          survey?.end_date &&
          survey?.email_signature?.trim() &&
          survey?.questions?.length,
      );
  $: setupComplete =
    scopeConfigured && participantsConfigured && settingsConfigured;
  $: displayName = getDisplayName(survey?.name);
  $: projectInfo = [
    { name: "Participants", value: participants.length },
    { name: "Responses", value: completed },
    { name: "Response Rate", value: responseRate, unit: "%" },
    {
      name: "Survey Timeframe",
      value: formatTimeframe(survey?.start_date, survey?.end_date),
    },
  ];
  $: surveyHealthInfo = [
    { name: "Outstanding", value: outstanding },
    { name: "Pending", value: pending },
    { name: "Completed", value: completed },
    { name: "Tasks", value: surveyTasks.length },
    taskSurvey
      ? { name: "Feedback questions", value: feedbackKpis.length }
      : {
          name: "Clusters",
          value: analysisSnapshot?.cluster_total ?? (finished ? 0 : "-"),
        },
  ];
  $: phases = taskSurvey
    ? buildTaskSurveyPhases(
        surveyId,
        sourceProjectId,
        finished,
        outstanding,
        scopeConfigured,
        participantsConfigured,
        settingsConfigured,
        setupComplete,
      )
    : buildPhases(
    surveyId,
    survey,
    finished,
    surveyLocked,
    outstanding,
    scopeConfigured,
    participantsConfigured,
    settingsConfigured,
    setupComplete,
  );

  function getDisplayName(name = "") {
    if (taskSurvey) return name;
    if (name.startsWith("Operational Excellence Pulse 2026"))
      return "Demo Potential Scout";
    return name === "Demo" ? "Demo Potential Scout" : name;
  }

  function formatDate(value) {
    if (!value) return "-";
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime())
      ? value
      : new Intl.DateTimeFormat("en-GB").format(date);
  }

  function formatTimeframe(startDate, endDate) {
    if (!startDate && !endDate) return "-";
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  }

  function openPreview() {
    window.open(
      `${ROUTES.knex.survey}?preview=${surveyId}`,
      "_blank",
      "noopener,noreferrer",
    );
  }

  function openSetupSection(route) {
    window.location.assign(route);
  }

  function buildPhases(
    currentSurveyId,
    currentSurvey,
    isFinished,
    isSurveyLocked,
    outstandingCount,
    isScopeConfigured,
    areParticipantsConfigured,
    areSettingsConfigured,
    isSetupComplete,
  ) {
    if (!currentSurveyId) return [];
    const formRoute = `${ROUTES.knex.form}?survey=${currentSurveyId}`;
    const overviewRoute = `${ROUTES.knex.potentialScope.taskOverview}?survey=${currentSurveyId}`;
    const invitationsRoute = `${ROUTES.knex.potentialScope.manageInvitations}?survey=${currentSurveyId}`;
    return [
      {
        label: "Setup",
        icon: SettingsIcon,
        steps: [
          {
            name: "Configure Scope",
            description: "Define business areas and tasks of interest",
            done: isScopeConfigured,
            disabled: isSurveyLocked,
            onClick: () =>
              openSetupSection(`${formRoute}&step=0&section=scope`),
          },
          {
            name: "Select Participants",
            description: "Choose the people who should take part",
            done: areParticipantsConfigured,
            disabled: isSurveyLocked || !isScopeConfigured,
            onClick: () =>
              openSetupSection(`${formRoute}&step=4&section=participants`),
          },
          {
            name: "Survey Settings",
            description: "Configure language, timeframe and reminders",
            done: areSettingsConfigured,
            disabled: isSurveyLocked || !areParticipantsConfigured,
            onClick: () =>
              openSetupSection(`${formRoute}&step=5&section=settings`),
          },
        ],
      },
      {
        label: "Survey",
        icon: TaskRegistrationIcon,
        steps: [
          {
            name: "Preview Survey",
            description: "Review the participant experience",
            done: isFinished,
            disabled: !isSetupComplete,
            active: false,
            onClick: openPreview,
          },
          {
            name: "Manage Invitations",
            description: outstandingCount
              ? "Control the 24-hour invitation countdown"
              : "Review and send survey reminders",
            done: isSetupComplete && outstandingCount === 0,
            disabled: !isSetupComplete,
            active: false,
            onClick: () => openSetupSection(invitationsRoute),
          },
          {
            name: "Monitor Participation",
            description: "Review responses and close the survey when ready",
            done: isFinished,
            disabled: !isSetupComplete,
            active: false,
            url: `${overviewRoute}&view=participation`,
          },
        ],
      },
      {
        label: "Analysis",
        iconPath: "/img/assistant/icons/analytics.svg",
        steps: [
          {
            name: "Start Analysis",
            description: "Analyze completed survey results",
            done: false,
            disabled: !isFinished,
            active: isFinished,
            url: overviewRoute,
          },
        ],
      },
    ];
  }

  function buildTaskSurveyPhases(
    currentSurveyId,
    projectId,
    isFinished,
    outstandingCount,
    isScopeConfigured,
    areParticipantsConfigured,
    areSettingsConfigured,
    isSetupComplete,
  ) {
    if (!currentSurveyId) return [];
    const setupRoute = `${ROUTES.dam.taskfrequencymapping}?idproject=${projectId}`;
    const invitationsRoute = `${ROUTES.knex.potentialScope.manageInvitations}?survey=${currentSurveyId}`;
    return [
      {
        label: "Setup",
        icon: SettingsIcon,
        steps: [
          {
            name: "Select Tasks",
            description: "Choose the tasks participants are asked about",
            done: isScopeConfigured,
            disabled: !projectId,
            onClick: () => openSetupSection(setupRoute),
          },
          {
            name: "Feedback Questions",
            description: "Pick the KPIs the survey asks for",
            done: areSettingsConfigured,
            disabled: !projectId || !isScopeConfigured,
            onClick: () => openSetupSection(setupRoute),
          },
          {
            name: "Survey Delivery",
            description: "Configure timeframe, language and reminders",
            done: areParticipantsConfigured && areSettingsConfigured,
            disabled: !projectId || !isScopeConfigured,
            onClick: () => openSetupSection(setupRoute),
          },
        ],
      },
      {
        label: "Survey",
        icon: TaskRegistrationIcon,
        steps: [
          {
            name: "Manage Invitations",
            description: outstandingCount
              ? "Send the outstanding invitations"
              : "Review and send survey reminders",
            done: isSetupComplete && outstandingCount === 0,
            disabled: !isSetupComplete,
            onClick: () => openSetupSection(invitationsRoute),
          },
        ],
      },
      {
        label: "Analysis",
        iconPath: "/img/assistant/icons/analytics.svg",
        steps: [
          {
            name: "Task Feedback Results",
            description: "Average scores and improvement ideas per task",
            done: false,
            disabled: !projectId,
            active: isFinished,
            onClick: () => openSetupSection(setupRoute),
          },
        ],
      },
    ];
  }

  async function updateProgressCardHeight() {
    if (!progressSectionElement) return;

    progressCardHeight = "";
    await tick();

    const cards = Array.from(
      progressSectionElement.querySelectorAll(".card-container .info_card"),
    );
    if (!cards.length) return;

    progressCardHeight = `${Math.ceil(
      Math.max(...cards.map((card) => card.getBoundingClientRect().height)),
    )}px`;
  }

  async function loadProgress() {
    if (!surveyId) {
      loadError = true;
      loading = false;
      return;
    }
    loading = true;
    loadError = false;
    try {
      const surveyResponse = await fetchWithSession(
        `/api/knextractor/surveys/${surveyId}`,
      );
      if (!surveyResponse.ok) throw new Error("Survey could not be loaded");
      survey = await surveyResponse.json();

      const snapshotResponse = await fetchWithSession(
        `/api/knextractor/analysis/${surveyId}/snapshot`,
      );
      if (snapshotResponse.ok) {
        const snapshotData = await snapshotResponse.json();
        analysisSnapshot = snapshotData.snapshot || null;
      }
    } catch (error) {
      console.error("Potential Scout progress loading failed", error);
      loadError = true;
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    loadProgress();
    window.addEventListener("resize", updateProgressCardHeight);
    return () => window.removeEventListener("resize", updateProgressCardHeight);
  });

  $: phases, updateProgressCardHeight();
</script>

<Header
  projectNameOverride={displayName}
  projectHref={ROUTES.dam.root}
  breadcrumb="Project Progress"
  tooltipText="Configure, manage and analyze this Potential Scout project."
/>

<SectionWrapper fitContent>
  <div
    slot="main"
    class="progress-section"
    bind:this={progressSectionElement}
    style={progressCardHeight
      ? `--progress-card-height: ${progressCardHeight};`
      : ""}
  >
    {#if loading}
      <div class="page-state">Loading project progress...</div>
    {:else if loadError}
      <div class="page-state error">Project progress could not be loaded.</div>
    {:else}
      <ProgressIndicator vertical>
        {#each phases as phase}
          <ProgressStep
            label={phase.label}
            interactive={false}
            totalSteps={phase.steps.length}
            completedSteps={phase.steps.filter((step) => step.done).length}
          >
            <svelte:fragment slot="icon">
              {#if phase.iconPath}
                <img class="phase-icon" src={phase.iconPath} alt="" />
              {:else}
                <svelte:component this={phase.icon} />
              {/if}
            </svelte:fragment>
            <div class="card-container">
              {#each phase.steps as step}
                <div class:disabled-card={step.disabled} class="card-wrapper">
                  <CardColumn
                    href={step.disabled ? undefined : step.url}
                    onClick={step.disabled ? undefined : step.onClick}
                    black={step.active}
                    style={!step.active
                      ? step.disabled
                        ? "background: #f2f2f2; opacity: 0.5; cursor: not-allowed;"
                        : step.done
                          ? "background: #8ef09580;"
                          : "background: #f2f2f2;"
                      : ""}
                  >
                    <div class="icon-label">
                      {#if step.disabled && !step.done}
                        <CheckXIcon /> <span>Locked</span>
                      {:else if step.done}
                        <CheckIcon /> <span>Done</span>
                      {:else}
                        <CheckEmptyIcon /> <span>Available</span>
                      {/if}
                    </div>
                    <p class="step-name">{step.name}</p>
                    <p class="step-description">{step.description}</p>
                  </CardColumn>
                </div>
              {/each}
            </div>
          </ProgressStep>
        {/each}
      </ProgressIndicator>
    {/if}
  </div>

  <div slot="menu" class="project-info">
    <Accordion heading="Project Metrics" open={true}>
      <div class="project-info">
        {#each projectInfo as info}
          <SideKPI name={info.name} value={info.value} unit={info.unit || ""} />
        {/each}
      </div>
    </Accordion>
    <Accordion heading="Survey Health" open={true}>
      <div class="project-info">
        {#each surveyHealthInfo as info}
          <SideKPI name={info.name} value={info.value} />
        {/each}
      </div>
    </Accordion>
  </div>
</SectionWrapper>

<style>
  .progress-section,
  .project-info {
    display: flex;
    flex-direction: column;
  }

  .progress-section {
    width: 100%;
  }

  .project-info {
    gap: 0.5rem;
  }

  .card-container {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1rem;
    margin-top: 1rem;
  }

  .card-wrapper,
  .card-container :global(.info_card_link) {
    display: flex;
    height: 100%;
    min-width: 0;
    width: 100%;
  }

  .card-container :global(.info_card) {
    height: 100%;
    width: 100%;
    min-height: var(--progress-card-height, auto);
    border-radius: 0.5rem;
    box-shadow: none;
    justify-content: flex-start;
    gap: 0.625rem;
    outline: 1px solid transparent;
    transition: outline-color 0.15s ease;
  }

  .card-container :global(.info_card_link:hover .info_card),
  .card-container :global(.info_card:hover) {
    box-shadow: none !important;
    outline-color: var(--pax-black);
    transform: none !important;
  }

  .disabled-card :global(.info_card:hover) {
    outline-color: transparent;
  }

  .icon-label {
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    gap: 0.375rem;
    color: #525252;
  }

  .icon-label span {
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  :global(.info_card_black) .icon-label {
    color: #f9fbff;
  }

  .step-name,
  .step-description {
    margin: 0;
  }

  .step-name {
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.25;
  }

  .step-description {
    color: #4d5358;
    font-size: 0.8125rem;
    line-height: 1.35;
  }

  :global(.info_card_black) .step-description {
    color: #e5e7eb;
  }

  .phase-icon {
    width: 1.25rem;
    height: 1.25rem;
    filter: brightness(0);
  }

  .page-state {
    padding: 2rem;
    color: var(--pax-sub);
  }

  .page-state.error {
    color: var(--pax-red);
  }

</style>
